import PropTypes from 'prop-types';
import { useRef, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';

// project imports
import Avatar from 'components/@extended/Avatar';
import Transitions from 'components/@extended/Transitions';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import ProfileOutlined from '@ant-design/icons/ProfileOutlined';
import WalletOutlined from '@ant-design/icons/WalletOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import BankOutlined from '@ant-design/icons/BankOutlined';
import SwapOutlined from '@ant-design/icons/SwapOutlined';
// import avatar1 from 'assets/images/users/avatar-1.png';

import RoleSwitchDialog from 'components/RoleSwitchDialog';
import { AuthContext } from 'contexts/AuthContext';
import api from 'services/api';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const theme = useTheme();
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [roleSwitchOpen, setRoleSwitchOpen] = useState(false);
  const [fetchedRoles, setFetchedRoles] = useState([]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // Logout Function
  const handleLogout = async () => {
    try {
      await api.post('/users/logout');
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      logout();
      navigate("/login");
    }
  };

  let storedUser = null;
  try {
    const hasLocalToken = !!localStorage.getItem('token');
    const hasSessionToken = !!sessionStorage.getItem('token');
    const storage = (hasSessionToken && !hasLocalToken) ? sessionStorage : localStorage;
    storedUser = JSON.parse(storage.getItem("user") || "null");
  } catch {
    storedUser = null;
  }

  const profileUser = user || storedUser;

  useEffect(() => {
    api.get('/users/my-roles')
      .then((res) => {
        const roles = res?.data?.data?.roles || res?.data?.roles || [];
        if (roles.length > 0) setFetchedRoles(roles);
      })
      .catch(() => {});
  }, []);

  const availableRoles = (profileUser?.available_roles && profileUser.available_roles.length > 0)
    ? profileUser.available_roles
    : fetchedRoles;

  const profileImage =
    profileUser?.profile_logo ||
    profileUser?.avatar ||
    profileUser?.profile_photo ||
    profileUser?.profile_image ||
    '';

  const userRole = String(
    profileUser?.role?.role_name ||
    profileUser?.role?.name ||
    profileUser?.role_name ||
    (typeof profileUser?.role === 'string' ? profileUser.role : '') ||
    localStorage.getItem('role') ||
    sessionStorage.getItem('role') ||
    ''
  ).toLowerCase().replace(/[\s_-]+/g, '_');

  const isClinicAdmin = userRole === 'clinic_admin';
    
  const roleLabel = String(profileUser?.role || profileUser?.role_name || "Patient")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

  return (
    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
      <Tooltip title="Profile" disableInteractive>
        <ButtonBase
          sx={(theme) => ({
            p: 0.25,
            borderRadius: 1,
            '&:focus-visible': {
              outline: `2px solid ${theme.vars.palette.secondary.dark}`,
              outlineOffset: 2
            }
          })}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ p: 0.5 }}>
            <Avatar
              alt="profile user"
              src={profileImage}
              sx={{
                width: 44,
                height: 44,
                bgcolor: 'primary.lighter',
                color: 'primary.main',
                '&:hover': {
                  outline: '1px solid',
                  outlineColor: 'primary.main'
                }
              }}
            >
              {!profileImage && (profileUser?.full_name ? profileUser.full_name.charAt(0).toUpperCase() : profileUser?.username ? profileUser.username.charAt(0).toUpperCase() : 'U')}
            </Avatar>
            <Typography variant="subtitle1" sx={{ color: 'inherit', fontWeight: 600 }}>
              {profileUser?.full_name || profileUser?.username || "User"}
            </Typography>
          </Stack>
        </ButtonBase>
      </Tooltip>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper
              sx={(theme) => ({
                boxShadow: theme.customShadows?.z1 || '0 4px 24px rgba(0, 0, 0, 0.08)',
                width: 320,
                minWidth: 280,
                maxWidth: {
                  xs: 280,
                  md: 320
                },
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${theme.vars.palette.divider}`,
                bgcolor: theme.vars.palette.background.paper,
                backgroundImage: 'none',
                backdropFilter: 'blur(48px) saturate(250%)',
                WebkitBackdropFilter: 'blur(48px) saturate(250%)'
              })}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {/* Header profile details */}
                  <Box
                    sx={(theme) => ({
                      px: 2.5,
                      py: 2.5,
                      bgcolor: theme.vars.palette.primary.lighter || '#f0f6ff',
                      borderBottom: `1px solid ${theme.vars.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    })}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, flex: 1, zIndex: 1 }}>
                      <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                        <Avatar
                          alt="profile user"
                          src={profileImage}
                          sx={(theme) => ({
                            width: 52,
                            height: 52,
                            border: `2px solid ${theme.vars.palette.background.paper}`,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            bgcolor: theme.vars.palette.primary.main,
                            color: theme.vars.palette.primary.contrastText
                          })}
                        >
                          {!profileImage && (profileUser?.full_name ? profileUser.full_name.charAt(0).toUpperCase() : profileUser?.username ? profileUser.username.charAt(0).toUpperCase() : 'U')}
                        </Avatar>
                        <Box
                          sx={(theme) => ({
                            position: 'absolute',
                            bottom: 2,
                            right: 2,
                            width: 12,
                            height: 12,
                            bgcolor: theme.vars.palette.success.main,
                            border: `2px solid ${theme.vars.palette.background.paper}`,
                            borderRadius: '50%'
                          })}
                        />
                      </Box>
                      <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {profileUser?.full_name || localStorage.getItem("full_name") || sessionStorage.getItem("full_name") || profileUser?.username || localStorage.getItem("username") || sessionStorage.getItem("username") || "User"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {profileUser?.email || localStorage.getItem("email") || sessionStorage.getItem("email") || "user@example.com"}
                        </Typography>
                        
                        {/* Role chip */}
                        <Box
                          sx={(theme) => ({
                            display: 'inline-flex',
                            alignItems: 'center',
                            bgcolor: theme.vars.palette.primary.light,
                            color: theme.vars.palette.primary.contrastText || '#fff',
                            borderRadius: '16px',
                            px: 1,
                            py: 0.25,
                            width: 'fit-content',
                            mt: 0.5
                          })}
                        >
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.2px', lineHeight: 1 }}>
                            {roleLabel}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Menu Items List */}
                  <Stack spacing={0.5} sx={{ p: 1.5 }}>
                    {[
                      {
                        title: 'Profile',
                        icon: <UserOutlined style={{ fontSize: 18 }} />,
                        onClick: () => navigate('/profile')
                      },
                      ...(availableRoles.length > 1
                        ? [
                            {
                              title: `Switch Role / Clinic (${availableRoles.length})`,
                              icon: <SwapOutlined style={{ fontSize: 18, color: '#1677ff' }} />,
                              onClick: () => setRoleSwitchOpen(true),
                              isSpecial: true
                            }
                          ]
                        : []),
                      {
                        title: 'Edit Profile',
                        icon: <EditOutlined style={{ fontSize: 18 }} />,
                        onClick: () => navigate('/profile/edit')
                      },
                      ...(isClinicAdmin
                        ? [
                            {
                              title: 'Clinic Profile',
                              icon: <BankOutlined style={{ fontSize: 18 }} />,
                              onClick: () => navigate('/clinic/profile')
                            }
                          ]
                        : []),
                      {
                        title: 'Billing',
                        icon: <WalletOutlined style={{ fontSize: 18 }} />,
                        onClick: () => navigate('/profile')
                      },
                      {
                        title: 'System Settings',
                        icon: <SettingOutlined style={{ fontSize: 18 }} />,
                        onClick: () => navigate('/settings')
                      }
                    ].map((item, index) => (
                      <ButtonBase
                        key={index}
                        onClick={(e) => {
                          item.onClick();
                          handleClose(e);
                        }}
                        sx={(theme) => ({
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          p: 1.25,
                          borderRadius: 1,
                          textAlign: 'left',
                          transition: 'all 0.2s ease-in-out',
                          color: item.isSpecial ? '#1677ff' : theme.vars.palette.text.primary,
                          bgcolor: item.isSpecial ? 'rgba(22, 119, 255, 0.08)' : 'transparent',
                          '&:hover': {
                            bgcolor: item.isSpecial ? 'rgba(22, 119, 255, 0.16)' : theme.vars.palette.action.hover,
                            color: theme.vars.palette.primary.main
                          }
                        })}
                      >
                        <Stack direction="row" spacing={1.75} alignItems="center">
                          <Box sx={{ color: 'inherit', display: 'flex' }}>
                            {item.icon}
                          </Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: item.isSpecial ? 700 : 500 }}>
                            {item.title}
                          </Typography>
                        </Stack>
                      </ButtonBase>
                    ))}

                    <Divider sx={{ my: 1 }} />

                    {/* Logout Option */}
                    <ButtonBase
                      onClick={(e) => {
                        handleLogout();
                        handleClose(e);
                      }}
                      sx={(theme) => ({
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        p: 1.25,
                        borderRadius: 1,
                        textAlign: 'left',
                        transition: 'all 0.2s ease-in-out',
                        color: theme.vars.palette.error.main,
                        '&:hover': {
                          bgcolor: theme.vars.palette.error.lighter,
                        }
                      })}
                    >
                      <Stack direction="row" spacing={1.75} alignItems="center">
                        <Box sx={{ color: 'inherit', display: 'flex' }}>
                          <LogoutOutlined style={{ fontSize: 18 }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                          Logout
                        </Typography>
                      </Stack>
                    </ButtonBase>
                  </Stack>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      <RoleSwitchDialog
        open={roleSwitchOpen}
        onClose={() => setRoleSwitchOpen(false)}
      />
    </Box>
  );
}
